// Algo to find circle spanned by 3 points
const circleDefBy3Points = (x1, y1, x2, y2, x3, y3) => {
  var x12 = x1 - x2;
  var x13 = x1 - x3;

  var y12 = y1 - y2;
  var y13 = y1 - y3;

  var y31 = y3 - y1;
  var y21 = y2 - y1;

  var x31 = x3 - x1;
  var x21 = x2 - x1;

  //x1^2 - x3^2
  var sx13 = x1 * x1 - x3 * x3;

  // y1^2 - y3^2
  var sy13 = y1 * y1 - y3 * y3;

  var sx21 = x2 * x2 - x1 * x1;
  var sy21 = y2 * y2 - y1 * y1;

  var f = Math.round(
    (sx13 * x12 + sy13 * x12 + sx21 * x13 + sy21 * x13) /
      (2 * (y31 * x12 - y21 * x13))
  );
  var g = Math.round(
    (sx13 * y12 + sy13 * y12 + sx21 * y13 + sy21 * y13) /
      (2 * (x31 * y12 - x21 * y13))
  );

  var c = -(x1 * x1) - y1 * y1 - 2 * g * x1 - 2 * f * y1;

  var h = -g;
  var k = -f;
  var sqr_of_r = h * h + k * k - c;
  var rad = Math.round(Math.sqrt(sqr_of_r));
  return { x: h, y: k, r: rad };
};

// shuffle array function
const shuffle = (array) => {
  let currentIndex = array.length,
    randomIndex;

  // While there remain elements to shuffle...
  while (currentIndex !== 0) {
    // Pick a remaining element...
    randomIndex = Math.floor(Math.random() * currentIndex);
    currentIndex--;

    // And swap it with the current element.
    [array[currentIndex], array[randomIndex]] = [
      array[randomIndex],
      array[currentIndex],
    ];
  }
  return array;
};
// sleep function
const sleep = (ms) => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};

// naive randomized circle algorithm, async
export async function calculateCircleAsync(
  points,
  setCircle,
  setIterations,
  numPoints,
  delay,
  stop,
  setIsCalculating
) {
  let it = 0;
  setIterations(it);

  do {
    setIsCalculating(true);
    var valid = true;
    it++;
    setIterations(it);
    let copy = [...points];
    copy = shuffle(copy);
    const currentPoints = copy.slice(0, numPoints);
    // calculate sec of the points
    var currentCircle = calculateCircleBF(currentPoints);
    const len = points.length;
    for (let i = 0; i < len; i++) {
      // check if it enclosed all points
      const tmp = Math.floor(
        (points[i].x - currentCircle.x) * (points[i].x - currentCircle.x) +
          (points[i].y - currentCircle.y) * (points[i].y - currentCircle.y)
      );
      if (tmp > currentCircle.r * currentCircle.r) {
        valid = false;
      }
    }
    setCircle({
      x: currentCircle.x,
      y: currentCircle.y,
      r: currentCircle.r,
    });
    await sleep(delay);

    if (stop.current) {
      setCircle({ x: 0, y: 0, r: 0 });
      return;
    }
  } while (!valid);
  setCircle({ x: currentCircle.x, y: currentCircle.y, r: currentCircle.r });
  setIsCalculating(false);
}

// naive randomized circle algorithm, instant calculation
export function calculateCircle(points, setCircle, setIterations, numPoints) {
  let it = 0;
  setIterations(it);

  do {
    var valid = true;
    it++;
    setIterations(it);
    let copy = [...points];
    copy = shuffle(copy);
    const currentPoints = copy.slice(0, numPoints);
    // calculate sec of the points
    var currentCircle = calculateCircleBF(currentPoints);
    const len = points.length;
    for (let i = 0; i < len; i++) {
      // check if it enclosed all points
      const tmp = Math.floor(
        (points[i].x - currentCircle.x) * (points[i].x - currentCircle.x) +
          (points[i].y - currentCircle.y) * (points[i].y - currentCircle.y)
      );
      if (tmp > currentCircle.r * currentCircle.r) {
        valid = false;
      }
    }
  } while (!valid);
  setCircle({ x: currentCircle.x, y: currentCircle.y, r: currentCircle.r });
}

// calculate circle method for small amounts of points
const calculateCircleBF = (points) => {
  let len = points.length;
  for (let i = 0; i < len; i++) {
    for (let j = i + 1; j < len; j++) {
      for (let k = j + 1; k < len; k++) {
        let valid = true;
        const tempCircle = calculateCircleBF3([
          points[i],
          points[j],
          points[k],
        ]);
        for (let l = 0; l < len; l++) {
          if (l !== i && l !== j && l !== k) {
            const tmp = Math.floor(
              (points[l].x - tempCircle.x) * (points[l].x - tempCircle.x) +
                (points[l].y - tempCircle.y) * (points[l].y - tempCircle.y)
            );
            if (tmp > tempCircle.r * tempCircle.r) {
              valid = false;
            }
          }
        }
        if (valid) {
          return tempCircle;
        }
      }
    }
  }
};
// sort 3 points in lexographical order by coordinates
const sortPoints = (points) => {
  return points.sort(sortP);
};
const sortP = (p1, p2) => {
  return compare(p1.x, p2.x) * 2 + compare(p1.y, p2.y);
};
const compare = (x, y) => {
  if (x === y) {
    return 0;
  } else if (x < y) {
    return -1;
  } else {
    return 1;
  }
};
// calculate circle method for 3 points
const calculateCircleBF3 = (points) => {
  const len = points.length;
  //first check circle spanned by pairs of points
  for (let i = 0; i < len; i++) {
    for (let j = i + 1; j < len; j++) {
      const tempX = Math.round((points[i].x + points[j].x) / 2);
      const tempY = Math.round((points[i].y + points[j].y) / 2);
      const tempR =
        Math.ceil(
          Math.sqrt(
            (points[i].x - points[j].x) * (points[i].x - points[j].x) +
              (points[i].y - points[j].y) * (points[i].y - points[j].y)
          ) / 2
        ) + 1;
      let valid = true;
      for (let k = 0; k < len; k++) {
        if (k !== i && k !== j) {
          const tmp = Math.floor(
            (points[k].x - tempX) * (points[k].x - tempX) +
              (points[k].y - tempY) * (points[k].y - tempY)
          );
          if (tmp > tempR * tempR) {
            valid = false;
          }
        }
      }
      if (valid) {
        return { x: tempX, y: tempY, r: tempR };
      }
    }
  }
  // if circles dont work for pairs, calculate 3-sets
  for (let i = 0; i < len; i++) {
    for (let j = i + 1; j < len; j++) {
      for (let k = j + 1; k < len; k++) {
        const tempPoints = sortPoints([points[i], points[j], points[k]]);
        const tempCircle = circleDefBy3Points(
          // points[i].x,
          // points[i].y,
          // points[j].x,
          // points[j].y,
          // points[k].x,
          // points[k].y
          tempPoints[0].x,
          tempPoints[0].y,
          tempPoints[1].x,
          tempPoints[1].y,
          tempPoints[2].x,
          tempPoints[2].y
        );
        tempCircle.r += 2;
        let valid = true;
        for (let l = 0; l < len; l++) {
          if (l !== i && l !== j && l !== k) {
            const tmp = Math.floor(
              (points[l].x - tempCircle.x) * (points[l].x - tempCircle.x) +
                (points[l].y - tempCircle.y) * (points[l].y - tempCircle.y)
            );
            if (tmp > tempCircle.r * tempCircle.r) {
              valid = false;
            }
          }
        }
        if (valid) {
          return { x: tempCircle.x, y: tempCircle.y, r: tempCircle.r };
        }
      }
    }
  }
};

// circle algos for smart method
// random point method from AnW script lemma 3.27
export const getRandomPoints = (points, numPoints, multiplicities) => {
  let res = [];
  let relevantMultiplicities = [];
  let len = points.length;
  for (let i = 0; i < len; i++) {
    relevantMultiplicities.push({
      m: multiplicities[points[i].id],
      id: points[i].id,
    });
  }

  for (let i = 0; i < numPoints; i++) {
    let N = relevantMultiplicities[0].m;
    for (let j = 1; j < relevantMultiplicities.length; j++) {
      N += relevantMultiplicities[j].m;
    }
    let k = Math.floor(Math.random() * (N - 1));
    let x = 0;
    let sum = relevantMultiplicities[0].m;
    while (sum < k) {
      x++;
      sum += relevantMultiplicities[x].m;
    }
    for (let k = 0; k < len; k++) {
      if (points[k].id === relevantMultiplicities[x].id) {
        res.push(points[k]);
        let temp = [];
        for (let b = 0; b < relevantMultiplicities.length; b++) {
          if (b !== x) temp.push(relevantMultiplicities[b]);
        }
        relevantMultiplicities = temp;
        break;
      }
    }
  }
  return res;
};

export async function calculateCircleAsyncSmart(
  points,
  setCircle,
  setIterations,
  numPoints,
  delay,
  stop,
  multiplicities,
  setMultiplicities,
  setIsCalculating
) {
  setIsCalculating(true);
  let it = 0;
  setIterations(it);
  do {
    const len = points.length;
    var valid = true;
    it++;
    setIterations(it);
    const currentPoints = getRandomPoints(points, numPoints, multiplicities);
    // calculate sec of the points
    var currentCircle = calculateCircleBF(currentPoints);
    for (let i = 0; i < len; i++) {
      // check if it enclosed all points
      const tmp = Math.floor(
        (points[i].x - currentCircle.x) * (points[i].x - currentCircle.x) +
          (points[i].y - currentCircle.y) * (points[i].y - currentCircle.y)
      );
      if (tmp > currentCircle.r * currentCircle.r) {
        valid = false;
        let m = multiplicities;
        let l = multiplicities.length;
        for (let p = 0; p < l; p++) {
          if (p === points[i].id) {
            m[p] = multiplicities[p] * 2;
          } else {
            m[p] = multiplicities[p];
          }
        }
        setMultiplicities(m);
      }
    }
    setCircle({
      x: currentCircle.x,
      y: currentCircle.y,
      r: currentCircle.r,
    });
    await sleep(delay);

    if (stop.current) {
      setCircle({ x: 0, y: 0, r: 0 });
      return;
    }
  } while (!valid);
  setCircle({ x: currentCircle.x, y: currentCircle.y, r: currentCircle.r });
  setIsCalculating(false);
}

export async function calculateCircleSmart(
  points,
  setCircle,
  setIterations,
  numPoints,
  multiplicities,
  setMultiplicities
) {
  let it = 0;
  setIterations(it);
  do {
    const len = points.length;
    var valid = true;
    it++;
    setIterations(it);
    const currentPoints = getRandomPoints(points, numPoints, multiplicities);
    // calculate sec of the points
    var currentCircle = calculateCircleBF(currentPoints);
    for (let i = 0; i < len; i++) {
      // check if it enclosed all points
      const tmp = Math.floor(
        (points[i].x - currentCircle.x) * (points[i].x - currentCircle.x) +
          (points[i].y - currentCircle.y) * (points[i].y - currentCircle.y)
      );
      if (tmp > currentCircle.r * currentCircle.r) {
        valid = false;
        let m = multiplicities;
        let l = multiplicities.length;
        for (let p = 0; p < l; p++) {
          if (p === points[i].id) {
            m[p] = multiplicities[p] * 2;
          } else {
            m[p] = multiplicities[p];
          }
        }
        setMultiplicities(m);
      }
    }
  } while (!valid);
  setCircle({ x: currentCircle.x, y: currentCircle.y, r: currentCircle.r });
}
